/**
 * Iter 67bs — Inline attribution chips for booking / car / invoice cards.
 *
 * Renders compact "Created by X · Edited by Y · Cancelled by Z" pills based
 * on the *_by_name + *_at fields the backend stamps on each entity. Hides
 * any action that wasn't recorded, so the strip naturally degrades on
 * legacy rows that pre-date Iter 67br.
 *
 * Usage:
 *   <AttributionChips entity={booking} actions={["create","update","cancel"]} />
 *   <AttributionChips entity={car} actions={["create","update"]} compact />
 */
import { UserCog, UserPlus, UserX, BadgeCheck, Receipt, FileSignature } from "lucide-react";

const ACTION_META = {
  create:  { label: "Created by",   tone: "bg-emerald-50 text-emerald-700 ring-emerald-200", icon: UserPlus },
  update:  { label: "Edited by",    tone: "bg-blue-50 text-blue-700 ring-blue-200",          icon: UserCog },
  cancel:  { label: "Cancelled by", tone: "bg-rose-50 text-rose-700 ring-rose-200",          icon: UserX },
  approve: { label: "Approved by",  tone: "bg-violet-50 text-violet-700 ring-violet-200",    icon: BadgeCheck },
  refund:  { label: "Refunded by",  tone: "bg-amber-50 text-amber-700 ring-amber-200",       icon: Receipt },
  sign:    { label: "Signed by",    tone: "bg-violet-50 text-violet-700 ring-violet-200",    icon: FileSignature },
  void:    { label: "Voided by",    tone: "bg-rose-50 text-rose-700 ring-rose-200",          icon: UserX },
};

function _fmtTs(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso.slice(0, 16).replace("T", " ");
  }
}

export default function AttributionChips({
  entity,
  actions = ["create", "update", "cancel"],
  compact = false,
  className = "",
}) {
  if (!entity) return null;
  const rows = actions.map((a) => {
    const name = entity[`${a}_by_name`];
    const at = entity[`${a}_at`];
    return name ? { action: a, name, at } : null;
  }).filter(Boolean);
  if (rows.length === 0) return null;
  return (
    <div
      className={`flex flex-wrap items-center gap-1.5 ${className}`}
      data-testid="attribution-chips"
    >
      {rows.map(({ action, name, at }) => {
        const meta = ACTION_META[action] || ACTION_META.update;
        const Icon = meta.icon;
        return (
          <span
            key={action}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md ring-1 ${meta.tone} text-[10px] font-bold uppercase tracking-wider`}
            title={`${meta.label} ${name}${at ? ` · ${_fmtTs(at)}` : ""}`}
            data-testid={`attribution-chip-${action}`}
          >
            <Icon className="h-3 w-3" />
            <span>{compact ? name : `${meta.label} ${name}`}</span>
            {!compact && at && <span className="text-[9px] font-medium opacity-70 ml-0.5">{_fmtTs(at)}</span>}
          </span>
        );
      })}
    </div>
  );
}
